/**
 * Browser-side query transport.
 *
 * Each `sbi.data.query(uuid, params)` from the worker becomes a `POST
 * /api/query/batch` request carrying a Bearer insight token. The
 * UUID was injected at source-serve time by the server's AST
 * rewriter — the SQL itself never crosses the wire from FE → BE,
 * so a tampered query message can't smuggle different SQL into
 * MotherDuck. The most an attacker can do is replay a UUID they've
 * already been issued, which is already scoped to their own token.
 *
 * Coalescing: every `send()` call enqueues onto a per-microtask
 * batch. After the synchronous burst of `sbi.data.query()` calls
 * settles (i.e. on the next microtask boundary), the queue is
 * dispatched as a single batch request. This collapses N round
 * trips on first paint into one — the structural cost a 2-phase
 * "load HTML, then SQL" flow needs. State-change reruns that fire
 * one query at a time still get a single-item batch, no special
 * casing required.
 *
 * Streaming: the batch response is **NDJSON** — one JSON object per
 * query result, one per line, written by the server as each query
 * resolves. The client reads the body as a `ReadableStream`, parses
 * each line as it arrives, and dispatches the worker message
 * immediately rather than waiting for the whole batch. Combined
 * with the worker's per-leaf patch path, that means an 8-widget
 * dashboard with one slow query renders the other 7 charts in
 * milliseconds instead of all waiting on the slowest.
 *
 * Cancellation: each batch carries its own `AbortController`, all
 * tracked in `inFlight`. When the runtime sees a `state_set` from
 * the worker (filter change), it calls `cancelInFlight()` before
 * posting the `rerun` host message, so the worker re-runs against
 * fresh promises rather than racing the old ones. Aborted batches
 * are silently swallowed — we don't surface query errors to the
 * worker because it's about to discard the old run anyway. With
 * streaming the abort propagates through the body reader to the
 * server, which can stop writing further lines and release pool
 * slots for the rerun.
 */

import type { HostMessage, WorkerMessage } from "./protocol.ts";
import { newExId } from "./exId.ts";

/** Narrow alias — the worker's "query" variant. Inlined for clarity. */
type QueryMessage = Extract<WorkerMessage, { type: "query" }>;
/** Host messages produced by this transport in response to a query. */
type QueryResultMessage = Extract<HostMessage, { type: "query_result" }>;
type QueryErrorMessage = Extract<HostMessage, { type: "query_error" }>;

/**
 * Per-query outcome shape coming back from `/api/query/batch`.
 *
 * `sql` is the resolved query text echoed by the server so the embed's
 * Queries tab can show the real SQL instead of the registry UUID the
 * worker dispatched. Absent on errors that fail pre-resolution
 * (`unknown_query_id`, `missing_query`).
 */
type BatchItem =
  | {
      id?: string;
      status: "ok";
      rows: Record<string, unknown>[];
      truncated?: boolean;
      sql?: string;
      /** photon's cache verdict. Host-only — see {@link QueryMeta}. */
      cached?: boolean;
    }
  | {
      id?: string;
      status: "error";
      code: string;
      message: string;
      sql?: string;
      /** Server-minted error correlation tag — surfaced in the embed UI as `[EX:<id>]`. */
      exId?: string;
    };

/**
 * Per-query diagnostics that reach the embed **host** and stop there.
 *
 * This is a deliberately separate channel from the `HostMessage` handed to
 * `onMessage`, and the separation is the entire safety property: the runner
 * forwards that message to the worker verbatim, so anything living on it
 * becomes reachable from insight source. A field here cannot be — it is passed
 * as a sibling argument the runner consumes for host chrome (the Queries tab)
 * and never posts onward.
 *
 * Keep it that way. If a diagnostic ever needs to reach the worker, that is a
 * conscious SBI surface change with a changelog entry, not a field quietly
 * moved from this type onto `query_result`.
 */
export interface QueryMeta {
  /**
   * True when photon served this statement from its cache. Absent on error
   * lines, on synthesised `missing_result` / `transport_error` lines, and
   * against a server too old to send it.
   */
  cached?: boolean;
}

/** Inputs the client needs to talk to the API. */
export interface HttpClientOptions {
  /** Base URL of `insights-api` (e.g. `https://insights-api.example.com`). */
  apiUrl: string;
  /** Bearer token scoped to a single insight. */
  token: string;
  /** The insight id this client is bound to. Used as `aud` audit anchor. */
  insightId: string;
  /**
   * Called once per query response — runtime forwards `msg` to the worker.
   *
   * `meta` carries host-only diagnostics for the same query and is **not**
   * part of the worker-bound message; see {@link QueryMeta}.
   */
  onMessage: (msg: HostMessage, meta?: QueryMeta) => void;
  /** Catch-all error sink. Network errors, 5xx, JSON-parse failures. */
  onError: (err: Error) => void;
  /**
   * Optional `fetch` override. Tests inject a stub that records the
   * request and returns canned JSON. Production uses the global.
   */
  fetchImpl?: typeof fetch;
}

/**
 * Transport bound to a single insight token.
 *
 * `send()` enqueues a `QueryMessage` for the next microtask; the
 * resulting batch hits `POST /api/query/batch` and dispatches each
 * item back through `onMessage`. `close()` aborts every batch in
 * flight and refuses future enqueues. `cancelInFlight()` aborts
 * pending batches but keeps the transport open — the runtime calls
 * this on every state change.
 */
export class HttpClient {
  private readonly opts: HttpClientOptions;
  private readonly fetchImpl: typeof fetch;
  private closed = false;

  /**
   * AbortControllers for batches currently waiting on the network.
   * Tracked as a set so one queue's `cancelInFlight()` can fan out
   * to every concurrent batch.
   */
  private readonly inFlight = new Set<AbortController>();

  /**
   * Microtask-coalesced batch queue. While a batch is "open" (we've
   * scheduled a flush but haven't fired the fetch yet) every
   * incoming `send()` joins this queue. Set back to `null` the
   * moment we drain it so the next `send()` opens a fresh batch.
   */
  private pendingQueue: QueryMessage[] | null = null;

  constructor(opts: HttpClientOptions) {
    this.opts = opts;
    // The browser's `fetch` is a method on `Window` and requires
    // `this` to BE `Window` at call time. Storing it as a class
    // property and invoking via `this.fetchImpl(...)` rebinds `this`
    // to the HttpClient instance and the browser throws "Failed to
    // execute 'fetch' on 'Window': Illegal invocation". Wrapping
    // the lookup in an arrow keeps `this` undefined at the call
    // site, which is what `fetch` wants.
    const resolved = opts.fetchImpl ?? globalThis.fetch;
    this.fetchImpl = (input, init) => resolved(input, init);
  }

  /**
   * Resolve once the transport is "ready" — for HTTP that's
   * immediate. We retain the method so the runtime's gating logic
   * (`workerReady && transportReady`) doesn't have to special-case
   * which transport it has.
   */
  ready(): Promise<void> {
    return Promise.resolve();
  }

  /**
   * Enqueue a worker message that needs a server round-trip. Today
   * only `query` does — `state_set` is handled locally by the
   * runtime. Errors hit `onError`; query-specific errors are routed
   * back to the worker as `query_error` messages. Aborted batches
   * (from `cancelInFlight()` / `close()`) are silently dropped.
   */
  send(msg: { type: string } & Record<string, unknown>): Promise<void> {
    if (msg.type !== "query") return Promise.resolve(); // state_set handled locally
    if (this.closed) return Promise.resolve();
    const query = msg as unknown as QueryMessage;

    // Open the batch if we don't have one yet, scheduling the flush
    // for the next microtask. Subsequent `send()` calls within the
    // same synchronous burst land in the same batch.
    if (this.pendingQueue === null) {
      this.pendingQueue = [];
      queueMicrotask(() => {
        void this.flushBatch();
      });
    }
    this.pendingQueue.push(query);
    return Promise.resolve();
  }

  /**
   * Abort every batch currently in flight without closing the
   * transport. Called by the runtime on state changes (filter
   * toggles, programmatic `setState`) so a worker re-run starts from
   * a clean slate. New `send()` calls after this are accepted
   * normally.
   */
  cancelInFlight(): void {
    for (const c of this.inFlight) c.abort();
    this.inFlight.clear();
    // A batch that hasn't fired yet is still in `pendingQueue`. We
    // drop it silently — the worker is about to be told to rerun.
    this.pendingQueue = null;
  }

  /** Mark the client closed; any in-flight or pending batches are aborted. */
  close(): void {
    this.closed = true;
    this.cancelInFlight();
  }

  /**
   * Drain the current pending queue and dispatch as a single batch
   * call. Called from a microtask scheduled by `send()`; safe to
   * call when `pendingQueue` is empty (no-op).
   */
  private async flushBatch(): Promise<void> {
    const queries = this.pendingQueue;
    this.pendingQueue = null;
    if (!queries || queries.length === 0 || this.closed) return;

    const controller = new AbortController();
    this.inFlight.add(controller);
    // Track which query ids have received a response line — at end
    // of stream we synthesise `missing_result` errors for any that
    // didn't. Keeps the worker's `pendingQueries` map from leaking.
    const seen = new Set<string>();
    try {
      await this.dispatchBatchStream(queries, controller.signal, (item) => {
        if (this.closed) return;
        if (typeof item.id === "string") seen.add(item.id);
        // Correlate by `id` — the server writes lines in completion
        // order, not request order, so positional alignment isn't
        // safe. Each query carries its `id` to/from the server for
        // exactly this reason.
        // `cached` rides alongside the message rather than on it — putting
        // it on `query_result` would publish it to the worker, and with it
        // to insight source. `itemToMessage` is what the worker sees.
        this.opts.onMessage(
          this.itemToMessage(item.id ?? "", item),
          item.status === "ok" && typeof item.cached === "boolean"
            ? { cached: item.cached }
            : undefined,
        );
      });
      if (this.closed) return;
      // Stream ended — fill in any missing query ids with a
      // synthesised error so every Worker promise resolves. Each gets
      // its own client-minted `exId` so support / devtools can locate
      // the exact slot that didn't get filled.
      for (const q of queries) {
        if (seen.has(q.id)) continue;
        const exId = newExId();
        console.error(
          `[EX:${exId}] stream ended without a result for query`,
          { id: q.id },
        );
        this.opts.onMessage({
          type: "query_error",
          id: q.id,
          code: "missing_result",
          message: "stream ended without a result for this query",
          exId,
        });
      }
    } catch (e) {
      // AbortError comes from `cancelInFlight()` / `close()` — the
      // worker is about to be told to rerun. Surfacing per-query
      // errors here would race the rerun and confuse the insight.
      if (controller.signal.aborted) return;
      const message = e instanceof Error ? e.message : String(e);
      // Network / fetch / stream failure: fan a transport error out
      // to every query that didn't already receive a line, so the
      // worker can resolve every pending promise. The transport
      // itself failed (no server log to grep), so we mint the `exId`
      // here and log it to the browser console — same tag the embed
      // UI will surface.
      const exId = newExId();
      console.error(`[EX:${exId}] transport error during batch`, e);
      for (const q of queries) {
        if (this.closed) break;
        if (seen.has(q.id)) continue;
        const errMsg: QueryErrorMessage = {
          type: "query_error",
          id: q.id,
          message,
          code: "transport_error",
          exId,
        };
        this.opts.onMessage(errMsg);
      }
    } finally {
      this.inFlight.delete(controller);
    }
  }

  /**
   * POST to `/api/query/batch`, then consume the NDJSON response body
   * line-by-line. For each parsed line, invoke `onItem` synchronously
   * so the runtime can wake its corresponding `pendingQueries` entry
   * the moment that one query lands — the slowest query no longer
   * gates rendering of the other 7.
   *
   * Stream-level failures (non-200 status, malformed body, network
   * cut) throw and let `flushBatch` fan the error across remaining
   * queries.
   */
  private async dispatchBatchStream(
    queries: ReadonlyArray<QueryMessage>,
    signal: AbortSignal,
    onItem: (item: BatchItem) => void,
  ): Promise<void> {
    const url = `${this.opts.apiUrl.replace(/\/$/, "")}/api/embed/query/batch`;
    const response = await this.fetchImpl(url, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${this.opts.token}`,
      },
      body: JSON.stringify({
        insight_id: this.opts.insightId,
        queries: queries.map((q) => ({
          // The `sql` field on QueryMessage carries the UUID injected
          // by source-serve — the runtime never sees raw SQL once
          // rewriting is in place. We send it as `query_id` so the
          // server's intent is unambiguous, and fall back to the
          // legacy `sql` field for tests/dev that bypass source-serve.
          id: q.id,
          query_id: q.sql,
          params: q.params,
        })),
      }),
      signal,
    });
    if (!response.ok) {
      const body = await response.text().catch(() => "");
      let message = body.slice(0, 200) || `HTTP ${response.status}`;
      try {
        const parsed = JSON.parse(body) as { message?: string };
        if (parsed.message) message = parsed.message;
      } catch {
        /* not JSON; use the raw text */
      }
      throw new Error(message);
    }
    if (!response.body) {
      // No streamable body — extremely unusual in modern browsers,
      // but undici test mocks sometimes return responses without a
      // ReadableStream. Treat as transport error so callers fan it
      // out to every query.
      throw new Error("response has no body to stream");
    }

    // Read the body as a stream of UTF-8 bytes, accumulate into a
    // line buffer, and emit each `\n`-terminated line. Trailing
    // partial line (no newline before EOF) is treated as a final
    // record so we don't drop the last item on an unterminated
    // stream — the server always writes a newline after each item
    // today, so this is defensive.
    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let buffer = "";
    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let nl = buffer.indexOf("\n");
        while (nl !== -1) {
          const line = buffer.slice(0, nl).trim();
          buffer = buffer.slice(nl + 1);
          if (line.length > 0) {
            // Parse + dispatch in the same tick the bytes arrived,
            // so the worker can start work on this item before the
            // next chunk lands.
            const item = JSON.parse(line) as BatchItem;
            onItem(item);
          }
          nl = buffer.indexOf("\n");
        }
      }
      // Flush any trailing partial line.
      const tail = buffer.trim();
      if (tail.length > 0) {
        const item = JSON.parse(tail) as BatchItem;
        onItem(item);
      }
    } finally {
      // Releasing the lock lets the underlying connection be reused
      // by future fetches (keep-alive); without this the connection
      // sits in a half-read state until GC.
      try {
        reader.releaseLock();
      } catch {
        /* reader already closed by abort */
      }
    }
  }

  /**
   * Convert one batch item into the worker-facing message shape.
   *
   * `sql`, when present, is forwarded so the host (which never sees raw
   * SQL otherwise — source-serve rewrote it to a UUID before bundling)
   * can populate the Queries debug tab with the real query text.
   */
  private itemToMessage(
    queryId: string,
    item: BatchItem,
  ): QueryResultMessage | QueryErrorMessage {
    if (item.status === "ok") {
      return {
        type: "query_result",
        id: queryId,
        rows: item.rows,
        ...(item.sql !== undefined ? { sql: item.sql } : {}),
      };
    }
    return {
      type: "query_error",
      id: queryId,
      message: item.message,
      code: item.code,
      ...(item.sql !== undefined ? { sql: item.sql } : {}),
      ...(item.exId !== undefined ? { exId: item.exId } : {}),
    };
  }
}
